/**
 * BabyBjörn main product — gallery, tabs slider, sticky bar.
 */
(function () {
  function getGalleryScroller(root) {
    return root.querySelector('[data-bbj-carousel-content]');
  }

  function getGallerySlides(root) {
    return root.querySelector('[data-bbj-slides]');
  }

  // Only slides that are laid out at the current breakpoint. The mobile-only
  // variant preview slide is display:none on desktop, so it must be excluded
  // from counting/indexing to keep slides and thumbs aligned.
  function isVisible(el) {
    return window.getComputedStyle(el).display !== 'none';
  }

  function getVisibleSlides(slides) {
    return Array.from(slides.children).filter(isVisible);
  }

  function getSlideIndex(slideEls, slide) {
    return slideEls.indexOf(slide);
  }

  // Computes the absolute scrollLeft that brings the slide flush to the
  // inline-start edge of the scroller. Measuring the visual delta with
  // getBoundingClientRect works identically for LTR and RTL (including
  // browsers that use negative scrollLeft in RTL).
  function getSlideScrollLeft(scroller, slideEls, index) {
    const slide = slideEls[index];
    if (!slide) return scroller.scrollLeft;

    const scrollerRect = scroller.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    return Math.round(scroller.scrollLeft + (slideRect.left - scrollerRect.left));
  }

  function scrollToSlide(scroller, slideEls, slide, behavior) {
    const index = getSlideIndex(slideEls, slide);
    if (index < 0 || !scroller) return;

    const left = getSlideScrollLeft(scroller, slideEls, index);
    if (behavior === 'auto') {
      scroller.scrollLeft = left;
      return;
    }

    scroller.scrollTo({
      left: left,
      behavior: behavior || 'smooth',
    });
  }

  function preservePageScroll(callback) {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    callback();
    window.scrollTo(scrollX, scrollY);
  }

  function restorePageScrollAfterLayout(scrollX, scrollY) {
    requestAnimationFrame(function () {
      window.scrollTo(scrollX, scrollY);
      requestAnimationFrame(function () {
        window.scrollTo(scrollX, scrollY);
      });
    });
  }

  function replacePreservingHeight(current, replacement) {
    const height = current.offsetHeight;
    if (height > 0) {
      current.style.minHeight = height + 'px';
    }
    const next = replacement.cloneNode(true);
    current.replaceWith(next);
    requestAnimationFrame(function () {
      next.style.minHeight = '';
    });
    return next;
  }

  function getClosestSlideIndex(scroller, slideEls) {
    const trackRect = scroller.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let closest = 0;
    let minDist = Infinity;

    slideEls.forEach(function (slide, i) {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const dist = Math.abs(slideCenter - trackCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });

    return closest;
  }

  // Horizontal scroll controls for the thumbnail strip. The track overflows
  // when there are more thumbs than fit at the current breakpoint; the arrows
  // page through it and disable at the edges. Uses Math.abs(scrollLeft) so the
  // same edge maths work in LTR and modern RTL (negative scrollLeft).
  function initThumbsArrows(root, thumbs) {
    const track = root.querySelector('[data-bbj-thumbs-track]');
    const prevBtn = root.querySelector('[data-bbj-thumbs-prev]');
    const nextBtn = root.querySelector('[data-bbj-thumbs-next]');
    if (!track || !prevBtn || !nextBtn) return null;

    const isRtl = window.getComputedStyle(track).direction === 'rtl';

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function updateArrows() {
      const max = maxScroll();
      const overflowing = max > 1;
      prevBtn.hidden = !overflowing;
      nextBtn.hidden = !overflowing;
      if (!overflowing) return;

      const pos = Math.abs(track.scrollLeft);
      const atLeading = pos <= 1;
      const atTrailing = pos >= max - 1;
      // "prev" moves toward the leading (start) edge, "next" toward the end.
      prevBtn.disabled = atLeading;
      nextBtn.disabled = atTrailing;
    }

    function page(towardEnd) {
      const step = Math.max(track.clientWidth * 0.75, 80);
      // scrollBy left is physical (positive = rightward); in RTL the end edge
      // is to the left, so advancing toward it means a negative delta.
      const physical = isRtl ? -step : step;
      track.scrollBy({ left: towardEnd ? physical : -physical, behavior: 'smooth' });
    }

    function scrollThumbIntoView(index) {
      const thumb = thumbs[index];
      if (!thumb || maxScroll() <= 1) return;
      const thumbRect = thumb.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      if (thumbRect.left < trackRect.left) {
        track.scrollBy({ left: thumbRect.left - trackRect.left, behavior: 'smooth' });
      } else if (thumbRect.right > trackRect.right) {
        track.scrollBy({ left: thumbRect.right - trackRect.right, behavior: 'smooth' });
      }
    }

    prevBtn.addEventListener('click', function () {
      page(false);
    });
    nextBtn.addEventListener('click', function () {
      page(true);
    });

    // Drag-to-scroll with a mouse. Touch and trackpad already scroll the track
    // natively (overflow-x), so only the mouse pointer is handled here.
    let isDown = false;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragged = false;

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      isDown = true;
      dragged = false;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', function (event) {
      if (!isDown) return;
      const dx = event.clientX - dragStartX;
      // Only start dragging past a small threshold. Capturing the pointer (and
      // marking the drag) is deferred until here so a plain click never gets
      // pointer-captured — which would retarget the click off the thumbnail and
      // stop it from switching the slider.
      if (!dragged) {
        if (Math.abs(dx) <= 3) return;
        dragged = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      }
      // Follow the pointer 1:1 — works for LTR and RTL alike.
      track.scrollLeft = dragStartScroll - dx;
    });

    function endDrag(event) {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      if (event && track.hasPointerCapture && track.hasPointerCapture(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Swallow the click that fires at the end of a drag so it does not also
    // select the thumbnail the pointer happens to be released on.
    track.addEventListener(
      'click',
      function (event) {
        if (dragged) {
          event.preventDefault();
          event.stopPropagation();
          dragged = false;
        }
      },
      true
    );

    let scrollTimer;
    track.addEventListener(
      'scroll',
      function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(updateArrows, 80);
      },
      { passive: true }
    );

    // Recompute on layout changes (breakpoint/track-width). Observing the track
    // rather than window keeps this scoped to the element, so it is released
    // when the gallery DOM is replaced on a variant change.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(updateArrows).observe(track);
    }

    updateArrows();

    return { updateArrows: updateArrows, scrollThumbIntoView: scrollThumbIntoView };
  }

  function initGallery(root, options) {
    const slides = getGallerySlides(root);
    const scroller = getGalleryScroller(root);
    const prevBtn = root.querySelector('[data-bbj-carousel-prev]');
    const nextBtn = root.querySelector('[data-bbj-carousel-next]');
    const currentEl = root.querySelector('[data-bbj-slide-current]');
    const totalEl = root.querySelector('[data-bbj-slide-total]');
    if (!slides || !scroller) return;

    // Exclude breakpoint-hidden slides/thumbs (e.g. the mobile-only variant
    // preview) so counting and slide↔thumb indexing stay in sync.
    const slideEls = getVisibleSlides(slides);
    const thumbs = Array.from(root.querySelectorAll('.babybjorn-main-product__thumb')).filter(isVisible);
    const slideCount = slideEls.length;
    if (totalEl) totalEl.textContent = String(slideCount);

    let activeIndex = 0;

    const thumbsArrows = initThumbsArrows(root, thumbs);

    function syncActiveState(index) {
      thumbs.forEach(function (t, i) {
        const on = i === index;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
      });

      if (currentEl) currentEl.textContent = String(index + 1);
      if (thumbsArrows) thumbsArrows.scrollThumbIntoView(index);
    }

    function goTo(index, goToOptions) {
      const behavior = goToOptions && goToOptions.behavior === 'auto' ? 'auto' : 'smooth';
      if (!slideCount) return;
      activeIndex = (index + slideCount) % slideCount;
      const slide = slideEls[activeIndex];
      if (!slide) return;

      scrollToSlide(scroller, slideEls, slide, behavior);
      syncActiveState(activeIndex);
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () {
        goTo(index);
      });
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(activeIndex - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(activeIndex + 1);
      });
    }

    let scrollTimer;
    scroller.addEventListener(
      'scroll',
      function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          const closest = getClosestSlideIndex(scroller, slideEls);
          if (closest !== activeIndex) {
            activeIndex = closest;
            syncActiveState(activeIndex);
          }
        }, 80);
      },
      { passive: true }
    );

    activeIndex = 0;
    syncActiveState(0);
    if (!options || !options.skipInitialScroll) {
      const firstSlide = slideEls[0];
      if (firstSlide) {
        scrollToSlide(scroller, slideEls, firstSlide, 'auto');
      }
    }
  }

  function updateTabSlider(tabsWrap) {
    const tablist = tabsWrap.querySelector('.babybjorn-main-product__tablist');
    const slider = tabsWrap.querySelector('[data-bbj-tab-slider]');
    if (!tablist || !slider) return;

    const activeTab = tablist.querySelector('.babybjorn-main-product__tab[aria-selected="true"]');
    if (!activeTab) return;

    slider.style.width = activeTab.offsetWidth + 'px';
    slider.style.transform = 'translateX(' + activeTab.offsetLeft + 'px)';
  }

  // A panel is considered empty when it renders no meaningful content: the tab
  // snippets (awards/carrying/manual) and the default content block output
  // nothing when their metafields/settings are blank, leaving an empty panel.
  function isPanelEmpty(panel) {
    if (!panel) return true;
    if (panel.textContent.trim() !== '') return false;
    return !panel.querySelector('img, picture, video, iframe, audio, svg, a[href]');
  }

  function initTabs(root) {
    const tabsWrap = root.querySelector('[data-bbj-tabs]');
    if (!tabsWrap) return;

    const tablist = tabsWrap.querySelector('.babybjorn-main-product__tablist');
    if (!tablist) return;

    const allTabs = Array.from(tablist.querySelectorAll('.babybjorn-main-product__tab[role="tab"]'));

    // Drop tabs whose panel has no content: hide the tab button and its panel.
    const tabs = [];
    const panels = [];
    allTabs.forEach(function (tab) {
      const id = tab.getAttribute('aria-controls');
      const panel = id ? document.getElementById(id) : null;
      if (isPanelEmpty(panel)) {
        const listItem = tab.closest('li');
        if (listItem) listItem.hidden = true;
        if (panel) panel.hidden = true;
        return;
      }
      tabs.push(tab);
      panels.push(panel);
    });

    if (!tabs.length) {
      tabsWrap.hidden = true;
      return;
    }

    function selectTab(index) {
      tabs.forEach(function (tab, i) {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (panels[i]) {
          if (on) panels[i].removeAttribute('hidden');
          else panels[i].setAttribute('hidden', '');
        }
      });
      updateTabSlider(tabsWrap);
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        selectTab(i);
      });
    });

    // If the originally-selected tab was dropped, activate the first visible one.
    const selectedIndex = tabs.findIndex(function (tab) {
      return tab.getAttribute('aria-selected') === 'true';
    });
    selectTab(selectedIndex >= 0 ? selectedIndex : 0);

    window.addEventListener('resize', function () {
      updateTabSlider(tabsWrap);
    });
  }

  function initSticky(root) {
    const sticky = root.querySelector('.babybjorn-main-product__sticky');
    const pane = root.querySelector('.babybjorn-main-product__pane');
    const stickyBtn = root.querySelector('[data-bbj-sticky-submit]');
    const mainBtn = root.querySelector('[data-bbj-main-submit]');
    if (!sticky || !pane) return;

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          sticky.classList.toggle('is-visible', !entry.isIntersecting);
          sticky.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
          sticky.toggleAttribute('inert', entry.isIntersecting);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -1px 0px' }
    );
    observer.observe(pane);

    if (stickyBtn && mainBtn) {
      stickyBtn.addEventListener('click', function () {
        mainBtn.click();
      });
    }

    const visibleSubmit = root.querySelector('[data-bbj-visible-submit]');
    if (visibleSubmit && mainBtn) {
      visibleSubmit.addEventListener('click', function () {
        mainBtn.click();
      });
    }
  }

  function initHotspots(root) {
    const container = root.querySelector('[data-bbj-hotspots]');
    if (!container || container.dataset.bbjHotspotsBound === '1') return;
    container.dataset.bbjHotspotsBound = '1';

    const triggers = container.querySelectorAll('[data-bbj-hotspot-trigger]');
    const badges = container.querySelectorAll('[data-bbj-badge-trigger]');

    function closeAll() {
      triggers.forEach(function (trigger) {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
        const popover = trigger.parentElement.querySelector('[data-bbj-hotspot-popover]');
        if (popover) popover.hidden = true;
      });
      badges.forEach(function (badge) {
        badge.classList.remove('is-active');
      });
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.stopPropagation();
        const popover = trigger.parentElement.querySelector('[data-bbj-hotspot-popover]');
        const isOpen = trigger.classList.contains('is-active');

        closeAll();

        if (!isOpen) {
          trigger.classList.add('is-active');
          trigger.setAttribute('aria-expanded', 'true');
          if (popover) popover.hidden = false;
        }
      });
    });

    badges.forEach(function (badge) {
      badge.addEventListener('click', function (event) {
        event.stopPropagation();
        const isOpen = badge.classList.contains('is-active');

        closeAll();

        if (!isOpen) badge.classList.add('is-active');
      });
    });

    document.addEventListener('click', function (event) {
      if (!container.contains(event.target)) closeAll();
    });
  }

  function getFetchedRoot(html, sectionId) {
    return (
      html.querySelector(`product-info[data-section="${sectionId}"]`) ||
      html.getElementById(`MainProduct-${sectionId}`) ||
      html.querySelector('[data-bbj-main-product]')
    );
  }

  function getProductRootForSection(sectionId) {
    return (
      document.querySelector(`product-info[data-section="${sectionId}"][data-bbj-main-product]`) ||
      document.getElementById(`MainProduct-${sectionId}`)
    );
  }

  function getFetchedHotspots(html, sectionId) {
    const fetchedRoot = getFetchedRoot(html, sectionId);
    return fetchedRoot?.querySelector('[data-bbj-hotspots]') || null;
  }

  function getFetchedGallery(html, sectionId) {
    const fetchedRoot = getFetchedRoot(html, sectionId);
    return fetchedRoot?.querySelector('[data-bbj-gallery]') || null;
  }

  function removeStyleDrawerPortal(sectionId) {
    document
      .querySelectorAll(
        `[data-bbj-style-overlay][data-bbj-style-section="${sectionId}"], [data-bbj-style-drawer][data-bbj-style-section="${sectionId}"]`
      )
      .forEach(function (el) {
        el.remove();
      });
  }

  function portalStyleDrawerElements(root) {
    const sectionId = root.dataset.section;
    if (!sectionId) return;

    const wrapper = root.querySelector('[data-bbj-style-wrapper]');
    const bodyOverlay = document.querySelector(`[data-bbj-style-overlay][data-bbj-style-section="${sectionId}"]`);
    const bodyDrawer = document.querySelector(`[data-bbj-style-drawer][data-bbj-style-section="${sectionId}"]`);

    if (!wrapper) {
      bodyOverlay?.remove();
      bodyDrawer?.remove();
      return;
    }

    const wrapperOverlay = wrapper.querySelector('[data-bbj-style-overlay]');
    const wrapperDrawer = wrapper.querySelector('[data-bbj-style-drawer]');

    if (wrapperOverlay) {
      bodyOverlay?.remove();
      wrapperOverlay.dataset.bbjStyleSection = sectionId;
      document.body.appendChild(wrapperOverlay);
    }

    if (wrapperDrawer) {
      bodyDrawer?.remove();
      wrapperDrawer.dataset.bbjStyleSection = sectionId;
      document.body.appendChild(wrapperDrawer);
    }
  }

  function getStyleDrawerElements(root) {
    const sectionId = root.dataset.section;

    return {
      overlay: document.querySelector(`[data-bbj-style-overlay][data-bbj-style-section="${sectionId}"]`),
      drawer: document.querySelector(`[data-bbj-style-drawer][data-bbj-style-section="${sectionId}"]`),
      trigger: root.querySelector('[data-bbj-style-trigger]'),
    };
  }

  function closeStyleDrawer(root) {
    const { drawer, overlay, trigger } = getStyleDrawerElements(root);

    drawer?.classList.remove('is-open');
    drawer?.setAttribute('aria-hidden', 'true');
    drawer?.setAttribute('hidden', '');
    overlay?.setAttribute('hidden', '');
    overlay?.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden');
  }

  function openStyleDrawer(root) {
    portalStyleDrawerElements(root);
    const { drawer, overlay, trigger } = getStyleDrawerElements(root);
    if (!drawer || !overlay) return;

    overlay.removeAttribute('hidden');
    overlay.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    drawer.focus();
  }

  function initStyleDrawer(root) {
    if (root.dataset.bbjStyleBound === '1') return;
    root.dataset.bbjStyleBound = '1';

    portalStyleDrawerElements(root);

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-bbj-style-trigger]')) {
        openStyleDrawer(root);
      }
    });
  }

  if (!window.__bbjStyleDrawerCloseBound) {
    window.__bbjStyleDrawerCloseBound = true;

    document.addEventListener('click', function (event) {
      const overlay = event.target.closest('[data-bbj-style-overlay]');
      const closeBtn = event.target.closest('[data-bbj-style-close]');
      if (!overlay && !closeBtn) return;

      const sectionId = (overlay || closeBtn?.closest('[data-bbj-style-drawer]'))?.dataset.bbjStyleSection;
      if (!sectionId) return;

      const root = getProductRootForSection(sectionId);
      if (root) closeStyleDrawer(root);
    });
  }

  function syncStyleBlock(root, html) {
    const sectionId = root.dataset.section;
    const openDrawer = document.querySelector(
      `[data-bbj-style-drawer][data-bbj-style-section="${sectionId}"]`
    );

    if (openDrawer?.classList.contains('is-open')) {
      closeStyleDrawer(root);
    }

    const fetchedRoot = getFetchedRoot(html, sectionId);
    const fetchedStyleWrapper = fetchedRoot?.querySelector('[data-bbj-style-wrapper]');
    const currentStyleWrapper = root.querySelector('[data-bbj-style-wrapper]');

    if (!fetchedStyleWrapper) {
      removeStyleDrawerPortal(sectionId);
      currentStyleWrapper?.remove();
      return;
    }

    const newStyleWrapper = fetchedStyleWrapper.cloneNode(true);

    if (currentStyleWrapper) {
      currentStyleWrapper.replaceWith(newStyleWrapper);
    } else {
      const variantPicker = root.querySelector('.babybjorn-main-product__variant-picker');
      const divider = root.querySelector('.babybjorn-main-product__divider');
      if (variantPicker) {
        variantPicker.appendChild(newStyleWrapper);
      } else if (divider) {
        divider.insertAdjacentElement('beforebegin', newStyleWrapper);
      }
    }

    portalStyleDrawerElements(root);
  }

  function syncVariantUi(root, html) {
    const sectionId = root.dataset.section;
    const fetchedRoot = getFetchedRoot(html, sectionId);

    if (!fetchedRoot) return;

    const fetchedHiddenSubmit = fetchedRoot.querySelector('[data-bbj-main-submit]');
    const currentHiddenSubmit = root.querySelector('[data-bbj-main-submit]');
    if (fetchedHiddenSubmit && currentHiddenSubmit) {
      currentHiddenSubmit.toggleAttribute('disabled', fetchedHiddenSubmit.hasAttribute('disabled'));
      const fetchedHiddenText = fetchedHiddenSubmit.querySelector('span');
      const currentHiddenText = currentHiddenSubmit.querySelector('span');
      if (fetchedHiddenText && currentHiddenText) {
        currentHiddenText.textContent = fetchedHiddenText.textContent;
      }
    }

    const fetchedVisibleSubmit = fetchedRoot.querySelector('[data-bbj-visible-submit]');
    const currentVisibleSubmit = root.querySelector('[data-bbj-visible-submit]');
    if (fetchedVisibleSubmit && currentVisibleSubmit) {
      currentVisibleSubmit.toggleAttribute('disabled', fetchedVisibleSubmit.hasAttribute('disabled'));
      currentVisibleSubmit.innerHTML = fetchedVisibleSubmit.innerHTML;
    }

    const fetchedStickySubmit = fetchedRoot.querySelector('[data-bbj-sticky-submit]');
    const currentStickySubmit = root.querySelector('[data-bbj-sticky-submit]');
    if (fetchedStickySubmit && currentStickySubmit) {
      currentStickySubmit.toggleAttribute('disabled', fetchedStickySubmit.hasAttribute('disabled'));
      currentStickySubmit.textContent = fetchedStickySubmit.textContent;
    }

    const fetchedStickyPrice = fetchedRoot.querySelector(`#StickyPrice-${sectionId}`);
    const currentStickyPrice = root.querySelector(`#StickyPrice-${sectionId}`);
    if (fetchedStickyPrice && currentStickyPrice) {
      currentStickyPrice.textContent = fetchedStickyPrice.textContent;
    }
  }

  function updateVariantGallery(root, html) {
    const sectionId = root.dataset.section;
    const fetchedGallery = getFetchedGallery(html, sectionId);
    const currentGallery = root.querySelector('[data-bbj-gallery]');
    if (!fetchedGallery || !currentGallery) return;

    replacePreservingHeight(currentGallery, fetchedGallery);
    initGallery(root, { skipInitialScroll: true });
    const slides = getGallerySlides(root);
    const scroller = getGalleryScroller(root);
    const slideEls = slides ? getVisibleSlides(slides) : [];
    const firstSlide = slideEls[0];
    if (scroller && firstSlide) {
      scrollToSlide(scroller, slideEls, firstSlide, 'auto');
    }
  }

  function updateVariantPreviewBlock(root, html) {
    const sectionId = root.dataset.section;
    const fetchedHotspots = getFetchedHotspots(html, sectionId);
    const galleryColumn = root.querySelector('.babybjorn-main-product__gallery-column');
    if (!galleryColumn) return;

    const currentHotspots = root.querySelector('[data-bbj-hotspots]');

    if (!fetchedHotspots) {
      currentHotspots?.remove();
      return;
    }

    if (currentHotspots) {
      replacePreservingHeight(currentHotspots, fetchedHotspots);
    } else {
      galleryColumn.insertBefore(fetchedHotspots.cloneNode(true), galleryColumn.firstChild);
    }

    initHotspots(root);
  }

  function normalizeVariantFilterValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function parseVariantFilterValues(raw) {
    if (!raw || raw === 'all') return null;
    return String(raw)
      .split(',')
      .map(normalizeVariantFilterValue)
      .filter(Boolean);
  }

  function getVariantFilterSwatchItems(root) {
    const fieldset = root.querySelector('.babybjorn-main-product__swatch-fieldset');
    if (!fieldset) return [];
    return Array.from(fieldset.querySelectorAll('[data-bbj-swatch-item]'));
  }

  function applyVariantFilter(root, filterValues) {
    const allowed = parseVariantFilterValues(filterValues);
    const items = getVariantFilterSwatchItems(root);
    if (!items.length) return;

    let firstVisibleInput = null;
    let checkedHidden = false;
    let checkedInput = null;

    items.forEach(function (item) {
      const input = item.querySelector('.swatch-input__input');
      if (!input) return;

      const value = normalizeVariantFilterValue(input.value);
      const show = !allowed || allowed.includes(value);
      item.classList.toggle('is-filter-hidden', !show);
      item.setAttribute('aria-hidden', show ? 'false' : 'true');

      if (show && !firstVisibleInput) {
        firstVisibleInput = input;
      }
      if (input.checked) {
        checkedInput = input;
        if (!show) checkedHidden = true;
      }
    });

    if (checkedHidden && firstVisibleInput && firstVisibleInput !== checkedInput) {
      firstVisibleInput.click();
    }
  }

  function setActiveVariantFilterButton(filterRoot, activeButton) {
    filterRoot.querySelectorAll('[data-bbj-variant-filter-btn]').forEach(function (button) {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function reapplyVariantFilter(root) {
    const filterRoot = root.querySelector('[data-bbj-variant-filter-root]');
    if (!filterRoot) return;

    const activeButton =
      filterRoot.querySelector('[data-bbj-variant-filter-btn].is-active') ||
      filterRoot.querySelector('[data-bbj-variant-filter-btn][data-bbj-variant-filter-values="all"]');

    if (!activeButton) return;

    applyVariantFilter(root, activeButton.dataset.bbjVariantFilterValues || 'all');
  }

  function initVariantFilter(root) {
    const filterRoot = root.querySelector('[data-bbj-variant-filter-root]');
    if (!filterRoot || filterRoot.dataset.bbjVariantFilterBound === '1') return;

    filterRoot.dataset.bbjVariantFilterBound = '1';

    filterRoot.addEventListener('click', function (event) {
      const button = event.target.closest('[data-bbj-variant-filter-btn]');
      if (!button || !filterRoot.contains(button)) return;

      setActiveVariantFilterButton(filterRoot, button);
      applyVariantFilter(root, button.dataset.bbjVariantFilterValues || 'all');
    });

    const defaultButton =
      filterRoot.querySelector('[data-bbj-variant-filter-btn][data-bbj-variant-filter-values="all"]') ||
      filterRoot.querySelector('[data-bbj-variant-filter-btn]');

    if (defaultButton) {
      setActiveVariantFilterButton(filterRoot, defaultButton);
      applyVariantFilter(root, defaultButton.dataset.bbjVariantFilterValues || 'all');
    }
  }

  function handleVariantPreviewChange(payload) {
    const sectionId = payload?.data?.sectionId;
    const html = payload?.data?.html;
    if (!sectionId || !html) return;

    const root = getProductRootForSection(String(sectionId));
    if (!root) return;

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const galleryColumn = root.querySelector('.babybjorn-main-product__gallery-column');
    const columnHeight = galleryColumn?.offsetHeight;

    if (galleryColumn && columnHeight > 0) {
      galleryColumn.style.minHeight = columnHeight + 'px';
    }

    preservePageScroll(function () {
      updateVariantGallery(root, html);
      updateVariantPreviewBlock(root, html);
      syncStyleBlock(root, html);
      syncVariantUi(root, html);
      reapplyVariantFilter(root);
    });

    restorePageScrollAfterLayout(scrollX, scrollY);

    requestAnimationFrame(function () {
      if (galleryColumn) {
        galleryColumn.style.minHeight = '';
      }
    });
  }

  subscribe(PUB_SUB_EVENTS.variantChange, handleVariantPreviewChange);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-bbj-main-product]').forEach(function (root) {
      closeStyleDrawer(root);
    });
  });

  function initRoot(root) {
    if (!root || root.dataset.bbjMainProductBound === '1') return;
    root.dataset.bbjMainProductBound = '1';
    initGallery(root);
    initTabs(root);
    initSticky(root);
    initHotspots(root);
    initStyleDrawer(root);
    initVariantFilter(root);
  }

  document.querySelectorAll('[data-bbj-main-product]').forEach(initRoot);

  document.addEventListener('shopify:section:load', function (event) {
    const root = event.target.querySelector('[data-bbj-main-product]');
    if (root) initRoot(root);
  });
})();
